/**
 * 放送地点マスタの一覧 + CRUD ページ。
 *
 * レイアウト:
 *   1. 上部: 線区/方面セレクタ + アクション (再読み込み / 新規作成 / Import/Export)
 *   2. 中段: 地図セクション (努力目標)
 *      - 線区が選択されているときだけ表示し、選択された路線を強調表示する。
 *      - 既存の放送地点をクリック可能なピンで描画し、クリックで編集モーダルを開く。
 *   3. 下段: 放送地点テーブル
 *
 * 編集 / 新規作成は BroadcastPointForm モーダル内で行い、保存時には ControlPanel API
 * (POST/PUT/DELETE) を即座に呼び出して結果を再取得する。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiError, base64ToAudioUrl, BroadcastPointsClient } from '../api/broadcastPoints';
import type { BroadcastContentCategory, BroadcastPoint } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import type { AppConfig } from '../config';
import {
  fetchRoutesMaster,
  parseRouteDirection,
  toRouteDirection,
  type RouteMaster,
} from '../data/routes';
import { fetchRouteShapePoints, type RouteShapePointsFile } from '../data/routeShapes';
import { findStationSegment, sortStopsBySequence, type RouteStop } from '../data/routeStops';
import { extractContents } from '../utils/broadcastContents';

import {
  BroadcastPointForm,
  type BroadcastPointFormValues,
} from '../components/BroadcastPointForm';
import { BroadcastPointsMap } from '../components/BroadcastPointsMap';
import { BroadcastPointsTable } from '../components/BroadcastPointsTable';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ImportExportControl } from '../components/ImportExportControl';
import { RouteSelector } from '../components/RouteSelector';

interface BroadcastPointsPageProps {
  config: AppConfig;
}

interface FormState {
  mode: 'create' | 'edit';
  initialValue: BroadcastPointFormValues;
}

const DEFAULT_RADIUS = 100;
const DEFAULT_BROADCAST_DELAY = 0;

type SortOrder = 'station_order' | 'created_at';
type ContentFilterValue = '' | BroadcastContentCategory;

export function BroadcastPointsPage(props: BroadcastPointsPageProps): React.JSX.Element {
  const { config } = props;
  const { getIdToken } = useAuth();

  const apiClient = useMemo(
    () => new BroadcastPointsClient({ getIdToken, config }),
    [getIdToken, config],
  );

  const [routes, setRoutes] = useState<RouteMaster[]>([]);
  /**
   * 駅順マスタ (route_id → 駅一覧)。接続線遅延コンテンツの開始/終了駅ドロップダウン用。
   * routes 読み込み後に ControlPanel API (`GET /route-stops`) からまとめて事前取得する。
   */
  const [routeStops, setRouteStops] = useState<Record<string, RouteStop[]>>({});

  /**
   * 選択中の方面に対応する線形 Point GeoJSON。
   * `null` (= GeoJSON 未配信) の時は地図側で線形を描画しない。
   */
  const [routeShapePoints, setRouteShapePoints] = useState<RouteShapePointsFile | null>(null);
  const [allPoints, setAllPoints] = useState<BroadcastPoint[]>([]);
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [selectedDirectionName, setSelectedDirectionName] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formState, setFormState] = useState<FormState | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('station_order');
  const [contentFilter, setContentFilter] = useState<ContentFilterValue>('');
  const [focusPoint, setFocusPoint] = useState<BroadcastPoint | null>(null);
  /**
   * 削除確認ダイアログの対象。null の場合はダイアログ非表示。
   * window.confirm の代わりにアプリ独自の ConfirmDialog を表示する。
   */
  const [deleteTarget, setDeleteTarget] = useState<BroadcastPoint | null>(null);

  const selectedRouteDirection = useMemo(() => {
    if (!selectedRouteId || !selectedDirectionName) return '';
    return toRouteDirection(selectedRouteId, selectedDirectionName);
  }, [selectedRouteId, selectedDirectionName]);

  // 表示対象の絞り込み (テーブル + 地図用 otherPoints)
  const filteredPoints = useMemo(() => {
    return allPoints.filter((p) => {
      if (selectedRouteId) {
        const { routeId } = parseRouteDirection(p.routeDirection);
        if (routeId !== selectedRouteId) return false;
      }
      if (selectedDirectionName) {
        const { directionName } = parseRouteDirection(p.routeDirection);
        if (directionName !== selectedDirectionName) return false;
      }
      return true;
    });
  }, [allPoints, selectedRouteId, selectedDirectionName]);

  /**
   * filteredPoints にコンテンツ種別フィルターとソートを適用した結果。
   * テーブル表示に使用する。地図は filteredPoints をそのまま使う。
   */
  const sortedFilteredPoints = useMemo(() => {
    // 1. コンテンツ種別フィルター
    const afterFilter = contentFilter
      ? filteredPoints.filter((p) => extractContents(p).some((c) => c.category === contentFilter))
      : filteredPoints;

    // 2. ソート
    const stops = selectedRouteId ? (routeStops[selectedRouteId] ?? []) : [];

    return afterFilter.slice().sort((a, b) => {
      if (sortOrder === 'station_order') {
        const seqA = getStationSequence(a, stops);
        const seqB = getStationSequence(b, stops);
        if (seqA !== seqB) return seqA - seqB;
        // 同順のときは routeDirection → spotId の辞書順で安定ソート
        if (a.routeDirection !== b.routeDirection)
          return a.routeDirection.localeCompare(b.routeDirection);
        return a.spotId.localeCompare(b.spotId);
      }
      // created_at: spotId (UUID v7) の辞書順 = 登録日時順
      return a.spotId.localeCompare(b.spotId);
    });
  }, [filteredPoints, contentFilter, sortOrder, selectedRouteId, routeStops]);

  // 初期データロード (routes-master.json のみ; 線形 GeoJSON は方面選択後に取得)
  useEffect(() => {
    let aborted = false;
    void (async () => {
      try {
        const rm = await fetchRoutesMaster();
        if (aborted) return;
        setRoutes(rm.routes);
      } catch (err) {
        if (!aborted) setErrorMessage((err as Error).message);
      }
    })();
    return () => {
      aborted = true;
    };
  }, []);

  // routes 読み込み後、全 route_id の駅順マスタを ControlPanel API からまとめて取得する。
  // 接続線遅延コンテンツの開始/終了駅ドロップダウンで利用する。
  // 取得に失敗した route_id は単にスキップする (ドロップダウンは空になるだけ)。
  useEffect(() => {
    let aborted = false;
    if (routes.length === 0) return;
    void (async () => {
      const entries = await Promise.all(
        routes.map(async (route) => {
          try {
            const data = await apiClient.getRouteStops(route.route_id);
            return [route.route_id, sortStopsBySequence(data.stops)] as const;
          } catch {
            // 駅順マスタ未登録 (404) 等は無視する
            return [route.route_id, [] as RouteStop[]] as const;
          }
        }),
      );
      if (aborted) return;
      setRouteStops(Object.fromEntries(entries));
    })();
    return () => {
      aborted = true;
    };
  }, [routes, apiClient]);

  // 選択中の線区/方面に対応する線形 GeoJSON を取得する。

  // - 線区/方面のいずれかが未選択 → null (= 線形を表示しない)
  // - GeoJSON が存在しない (404 等) → null (= 同上)
  useEffect(() => {
    let aborted = false;
    if (!selectedRouteId || !selectedDirectionName) {
      setRouteShapePoints(null);
      return;
    }
    // route/direction 切替時は一旦クリアして表示の取り違えを防ぐ
    setRouteShapePoints(null);
    void (async () => {
      const data = await fetchRouteShapePoints(selectedRouteId, selectedDirectionName);
      if (!aborted) setRouteShapePoints(data);
    })();
    return () => {
      aborted = true;
    };
  }, [selectedRouteId, selectedDirectionName]);

  // 一覧取得 (routeDirection 指定時は Query、未指定時は scan)
  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await apiClient.list(selectedRouteDirection || undefined);
      setAllPoints(res.items);
    } catch (err) {
      setErrorMessage(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [apiClient, selectedRouteDirection]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleCreate = (): void => {
    if (!selectedRouteDirection) return;
    setFormState({
      mode: 'create',
      initialValue: {
        routeDirection: selectedRouteDirection,
        spotId: undefined,
        description: '',
        latitude: undefined,
        longitude: undefined,
        radius_meters: DEFAULT_RADIUS,
        broadcast_delay_seconds: DEFAULT_BROADCAST_DELAY,
        contents: [],
      },
    });
  };

  const handleEdit = (point: BroadcastPoint): void => {
    setFormState({
      mode: 'edit',
      initialValue: {
        routeDirection: point.routeDirection,
        spotId: point.spotId,
        description: point.description ?? '',
        latitude: point.latitude,
        longitude: point.longitude,
        radius_meters: point.radius_meters ?? DEFAULT_RADIUS,
        broadcast_delay_seconds: point.broadcast_delay_seconds ?? DEFAULT_BROADCAST_DELAY,
        contents: extractContents(point),
      },
    });
  };

  /**
   * 編集モーダルから「コピーして新規作成」ボタンが押された時のハンドラ。
   *
   * モーダル内のコピー先選択ダイアログでユーザーが選んだ routeDirection を
   * 第 2 引数で受け取り、以下を初期値とする新規作成モーダルへ切り替える:
   *   - routeDirection: ユーザーが選択したコピー先 (元と異なる線区/方面も指定可)
   *   - 半径 / 放送遅延 / コンテンツ: そのまま引き継ぐ
   *   - spotId: undefined (新規採番させる)
   *   - 説明 / 緯度 / 経度: 空欄にしてユーザーに再入力させる
   *     (同一場所での重複登録を避けるため + 説明はコピー先の地点で書き直す前提)
   */
  const handleCopy = (values: BroadcastPointFormValues, routeDirection: string): void => {
    setFormState({
      mode: 'create',
      initialValue: {
        ...values,
        routeDirection,
        spotId: undefined,
        description: '',
        latitude: undefined,
        longitude: undefined,
      },
    });
  };

  /**
   * 削除ボタン押下時に呼ばれるハンドラ。
   * 確認ダイアログを表示するため対象を state に格納するだけで、実削除は
   * `executeDelete` (= ConfirmDialog の onConfirm) で行う。
   */
  const handleDelete = (point: BroadcastPoint): void => {
    setDeleteTarget(point);
  };

  /**
   * 削除確認ダイアログで OK が押されたときの実削除処理。
   */
  const executeDelete = async (): Promise<void> => {
    const point = deleteTarget;
    setDeleteTarget(null);
    if (!point) return;
    try {
      await apiClient.remove(point.spotId, point.routeDirection);
      await reload();
    } catch (err) {
      setErrorMessage(formatApiError(err));
    }
  };

  const handleFormSubmit = async (values: BroadcastPointFormValues): Promise<void> => {
    const payload = {
      routeDirection: values.routeDirection,
      description: values.description,
      latitude: values.latitude,
      longitude: values.longitude,
      radius_meters: values.radius_meters,
      broadcast_delay_seconds: values.broadcast_delay_seconds,
      contents: values.contents,
    };
    if (formState?.mode === 'edit' && values.spotId) {
      await apiClient.update(values.spotId, payload);
    } else {
      await apiClient.create(payload);
    }
    setFormState(null);
    await reload();
  };

  const handleImport = async (
    importItems: BroadcastPoint[],
  ): Promise<{ success: number; failures: string[] }> => {
    let success = 0;
    const failures: string[] = [];
    for (const item of importItems) {
      const payload = {
        routeDirection: item.routeDirection,
        description: item.description,
        latitude: item.latitude,
        longitude: item.longitude,
        radius_meters: item.radius_meters,
        broadcast_delay_seconds: item.broadcast_delay_seconds,
        contents: item.contents,
      };
      try {
        if (item.spotId) {
          await apiClient.update(item.spotId, payload);
        } else {
          await apiClient.create(payload);
        }
        success++;
      } catch (err) {
        failures.push(`${item.routeDirection}/${item.spotId || '(new)'}: ${formatApiError(err)}`);
      }
    }
    await reload();
    return { success, failures };
  };

  const showOverviewMap = Boolean(selectedRouteId);

  return (
    <>
      <section className="section">
        <h2>放送新規設定</h2>
        <RouteSelector
          routes={routes}
          selectedRouteId={selectedRouteId}
          selectedDirectionName={selectedDirectionName}
          onChange={(routeId, directionName) => {
            setSelectedRouteId(routeId);
            setSelectedDirectionName(directionName);
          }}
          onCreate={handleCreate}
          onReload={() => void reload()}
          extraActions={<ImportExportControl items={filteredPoints} onImport={handleImport} />}
        />
      </section>
      {showOverviewMap ? (
        <section className="section">
          <h2>地図 (路線・既存放送地点)</h2>
          <p className="map-help">
            選択された路線が地図上で強調表示されます。既存の放送地点 (青ピン) を
            クリックすると編集ダイアログが開きます。
          </p>
          <div className="overview-map">
            <BroadcastPointsMap
              otherPoints={filteredPoints}
              routeShapePoints={routeShapePoints}
              onPointClick={handleEdit}
              focusPoint={focusPoint}
            />
          </div>
        </section>
      ) : null}
      <section className="section">
        <h2>
          放送地点一覧{' '}
          {loading
            ? '(読み込み中...)'
            : sortedFilteredPoints.length === filteredPoints.length
              ? `(${filteredPoints.length} 件)`
              : `(${sortedFilteredPoints.length} / ${filteredPoints.length} 件)`}
        </h2>
        {errorMessage ? <div className="error-message">{errorMessage}</div> : null}
        <div className="sort-filter-bar">
          <div className="sort-filter-group">
            <span className="sort-filter-label">ソート</span>
            <label className="sort-filter-radio">
              <input
                type="radio"
                name="sortOrder"
                value="station_order"
                checked={sortOrder === 'station_order'}
                onChange={() => setSortOrder('station_order')}
              />
              駅並び順
            </label>
            <label className="sort-filter-radio">
              <input
                type="radio"
                name="sortOrder"
                value="created_at"
                checked={sortOrder === 'created_at'}
                onChange={() => setSortOrder('created_at')}
              />
              登録日時順
            </label>
          </div>
          <div className="sort-filter-group">
            <label className="sort-filter-select-label" htmlFor="content-filter">
              <span className="sort-filter-label">コンテンツ種別</span>
              <select
                id="content-filter"
                value={contentFilter}
                onChange={(e) => setContentFilter(e.target.value as ContentFilterValue)}
              >
                <option value="">すべて</option>
                <option value="self_train_delay">自列車遅延</option>
                <option value="connecting_line_delay">他線区遅延</option>
                <option value="free_text">任意テキスト</option>
              </select>
            </label>
          </div>
        </div>
        <BroadcastPointsTable
          points={sortedFilteredPoints}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRowClick={showOverviewMap ? setFocusPoint : undefined}
        />
      </section>
      {formState ? (
        <BroadcastPointForm
          mode={formState.mode}
          initialValue={formState.initialValue}
          otherPoints={filteredPoints.filter((p) => p.spotId !== formState.initialValue.spotId)}
          routeShapePoints={routeShapePoints}
          // 親が `routeShapePoints` をどの routeDirection 用に取得したかを明示する。
          // 「コピーして新規作成」でコピー元と異なる線区/方面が選ばれた際、
          // フォーム側でこの値と `initialValue.routeDirection` のズレを検知し、
          // 親値の代わりにフォーム側で自前 fetch した線形を使って地図を描画させる。
          routeShapePointsRouteDirection={selectedRouteDirection}
          routes={routes}
          routeStops={routeStops}
          onUploadAudio={async (file) => {
            const { audio_key } = await apiClient.uploadAudio(file);
            return audio_key;
          }}
          onPreviewSsml={async (input) => {
            const { content_type, content_base64 } = await apiClient.previewAudio({
              text: input.text,
              ssml: input.ssml,
              isSsml: input.isSsml,
            });
            return base64ToAudioUrl(content_base64, content_type);
          }}
          onSubmit={handleFormSubmit}
          onCopy={handleCopy}
          onClose={() => setFormState(null)}
        />
      ) : null}
      {/* 削除確認ダイアログ (window.confirm の代替) */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="放送地点の削除"
        message={`放送地点 "${deleteTarget?.description ?? '(名称未設定)'}" を削除します。よろしいですか？`}
        confirmLabel="削除"
        destructive
        onConfirm={() => void executeDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}

/** API クライアントの例外を画面表示用文字列に整形する */
function formatApiError(err: unknown): string {
  if (err instanceof ApiError) {
    return `[HTTP ${err.status}] ${err.message}`;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return '予期しないエラーが発生しました';
}

/**
 * 放送地点の座標から「最近傍区間の開始駅 stop_sequence」を返す。
 * 駅順マスタが空、または座標が未設定の場合は Infinity を返して末尾に送る。
 */
function getStationSequence(point: BroadcastPoint, stops: RouteStop[]): number {
  if (
    stops.length < 2 ||
    typeof point.latitude !== 'number' ||
    typeof point.longitude !== 'number'
  ) {
    return Infinity;
  }
  const segment = findStationSegment(stops, point.latitude, point.longitude);
  return segment?.startStop.stop_sequence ?? Infinity;
}
